import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useLazyQuery } from '@apollo/client'
import { Loader2, Plus, X, GripVertical, Trash2 } from 'lucide-react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useLanguage } from '@/context/LanguageContext'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import { RichTextField } from '@/components/shared/inputComponents/RichTextField'
import { ImageUpload } from '@/components/shared/inputComponents/ImageUpload'
import { ImageUploadV2 } from '@/components/shared/inputComponents/ImageUploadV2'
import { VideoUploadV2 } from '@/components/shared/inputComponents/VideoUploadV2'
import { PdfUploadV2 } from '@/components/shared/inputComponents/PdfUploadV2'
import {
  CREATE_CONTENT_BLOCK,
  CREATE_LINKED_CONTENT_BLOCK,
  GET_CONTENT_BLOCK,
  UPDATE_CONTENT_BLOCK,
  UPDATE_LINKED_CONTENT_BLOCK,
} from '../contentBlockQueries'
import { GET_CATEGORIES_FOR_SELECT } from '../../Categories/categoriesQueries'
import { ContentBlockFormValues } from '../types'
import {
  buildContentBlockVariables,
  contentBlockFormSchema,
  isLinkedContentBlockType,
  naturalTitleAlign,
  overlayProfileNames,
} from '../contentBlockPayload'
import { StyleSelector } from './StyleSelector'
import { StyleEditor } from './StyleEditor'
import { TitleAlignmentControl } from './TitleAlignmentControl'
import ContentBlockUsages from './ContentBlockUsages'
import GridProductPicker from './GridProductPicker'
import TagsSelect from '@/components/shared/inputComponents/TagsSelect'
import CtaEditor from '@/components/shared/CtaEditor'
import type { ContentBlockItem } from './DraggableContentBlockItem'
import {
  GET_PAGES_FOR_SELECTOR,
  PageSelector,
  imageTypes,
  videoTypes,
  videoTypeOptions,
  textStyles,
  galleryStyles,
  textAndImageStyles,
  buttonStyles,
  contentBlockTypes,
} from '../contentBlockConstants'

// --- Types ---

interface ContentBlockFormBodyProps {
  blockId: number | null
  onCreated: (block: ContentBlockItem) => void
  onUpdated: (block: ContentBlockItem) => void
  onCancel?: () => void
  /**
   * Hide the Description field from this settings form AND omit `description`
   * from the submit payload. Used by the page editor (BlockEditPanel), where the
   * description is edited inline in the full-size preview instead. Defaults to
   * false so the Dialog consumers (ContentBlocksManager, CategoryForm via
   * InlineContentBlockForm) keep rendering + sending Description as before.
   */
  hideDescription?: boolean
  /**
   * Tailwind classes controlling scroll behavior + height cap on this body's
   * own container. This is the *whole* scroll/height treatment, not just a
   * max-height — pass `''` to opt this container out of internal scrolling
   * entirely (e.g. when an ancestor, like the page document, should own the
   * scroll instead).
   * Defaults to `overflow-y-auto max-h-[70vh]`, which InlineContentBlockForm's
   * Dialog (`max-h-[90vh]` DialogContent) relies on to stay scrollable within
   * the viewport-bounded modal. The page editor (BlockEditPanel) passes `''`
   * so the form grows to its natural height and the page scrolls instead.
   */
  bodyScrollClassName?: string
  /**
   * STAGED mode (page editor only). When provided, this settings form no
   * longer mutates on its own: the Cancel/Update action row is replaced by a
   * short note, and every settings change is instead reported through this
   * callback (skipped during the initial load/reset, so merely opening a
   * block never marks it dirty) with the form's RAW current values —
   * `form.getValues()`, unfiltered and NOT run through the zod schema. This
   * must stay raw: `content_block_profiles` here is index-aligned with
   * `allLanguages` (the Title/Description fields address it BY INDEX, keyed
   * off `LanguageContext`'s positional `languageId`), and this same snapshot
   * is fed straight back into `form.reset()` if this block's panel is
   * reopened before the page Save flushes it (see `draft` below) — filtering
   * or otherwise transforming it here would shift every later language's
   * profile onto the wrong index (silent cross-language data corruption).
   * The caller (`PageDetails`) holds these in a client-side draft and, at
   * flush time, validates + filters them via `contentBlockFormSchema.safeParse`
   * (the SAME schema this form's resolver uses) immediately before building
   * the SAME `buildContentBlockVariables` payload — see
   * `PageDetails.flushBlockDraft`. A block whose staged draft still fails
   * that parse at flush time is reported as an ordinary per-block
   * save-what-works failure (named in a toast), not silently dropped or
   * allowed to corrupt another language's profile. Omitted (undefined, the
   * default) preserves today's immediate per-block mutation for the Dialog
   * consumers (ContentBlocksManager, CategoryForm via InlineContentBlockForm).
   *
   * The second argument is the per-language name THIS form was seeded with
   * (see `seededNameByLanguageRef`) — the caller needs it to tell a genuine
   * retype in this form's Title field apart from a settings-name that merely
   * looks stale because it was seeded before a newer inline override existed.
   */
  onDraftChange?: (
    values: ContentBlockFormValues,
    seededNameByLanguage: Record<string, string>
  ) => void
  /**
   * The block's previously staged settings-form snapshot (if any), used ONLY
   * in staged mode (see `onDraftChange`) to seed this form instead of the
   * freshly-fetched server data. Without this, closing and reopening the same
   * block's settings mid-session (before the page Save flushes it) would
   * reset the visible fields to server truth and a subsequent edit would
   * silently discard the earlier staged change when `onDraftChange` next
   * fires with a fresh-from-server snapshot.
   */
  draft?: ContentBlockFormValues | null
  /**
   * STAGED mode only. The block's staged inline-title overrides (per
   * language), i.e. `blockDrafts[id].nameByLanguage`. Folded onto whichever
   * source seeds this form (`draft` or the freshly-fetched server data) via
   * `overlayProfileNames` — the SAME overlay `PageDetails.flushBlockDraft`
   * applies at flush time — so opening the settings panel shows the title
   * the flush would actually send, not a stale value from before the inline
   * edit. This is a SEED-time computation only (deliberately excluded from
   * the population effect's deps below, alongside `draft`): it must not
   * re-fire and reset the form while the user is mid-edit here.
   */
  nameOverrides?: Record<string, string>
}

// --- Component ---

export default function ContentBlockFormBody({
  blockId,
  onCreated,
  onUpdated,
  onCancel,
  hideDescription = false,
  bodyScrollClassName = 'overflow-y-auto max-h-[70vh]',
  onDraftChange,
  draft,
  nameOverrides,
}: ContentBlockFormBodyProps) {
  const isStaged = Boolean(onDraftChange)
  const { allLanguages, languageId } = useLanguage()
  const isEditMode = blockId !== null

  // The FULL `config` object as loaded (edit) or `{}` (create). Retained so the
  // save can spread it FIRST when rebuilding config — the custom mutations null
  // config when it isn't sent, so keys the form doesn't rebuild (title settings,
  // grid keys, category_ids, …) must be carried through explicitly.
  const rawConfigRef = useRef<Record<string, unknown>>({})

  // Staged mode only: the per-language `name` this form was last SEEDED with
  // (i.e. `seedValues.content_block_profiles[i].name` — `baseValues` folded
  // with the `nameOverrides` overlay, exactly as the population effect below
  // sets it, captured at the same seed-time-only point `nameOverrides`
  // itself is read). Reported alongside every staged snapshot (see the
  // staging effect) so the caller can distinguish "the settings Title now
  // differs from an inline override because the user retyped it in THIS
  // form" from "...because this form was seeded before that override
  // existed" — comparing only to the (possibly stale) override conflates
  // those two cases. See `PageDetails.handleStageFormValues`.
  const seededNameByLanguageRef = useRef<Record<string, string>>({})

  // Staged mode only: a snapshot of the values `form.reset()` last established
  // as this mount's baseline, serialized with the SAME `JSON.stringify` used
  // for the staging effect's live-values comparison below — so the two are
  // apples-to-apples. Set at every `form.reset(...)` call in the population
  // effect below, right alongside `seededNameByLanguageRef`.
  //
  // The staging effect uses THIS (string equality against the live form
  // values) instead of `form.formState.isDirty` to decide whether anything
  // has changed since the seed. RHF's own dirty-tracking is NOT a reliable
  // signal here: `selected_page_ids` (the page_grid picker) is mutated via a
  // bare `form.setValue(...)` on a path that's never registered via
  // `FormField`/`Controller`/`useFieldArray` (see the page-selection handlers
  // below), and — depending on the installed react-hook-form version —
  // `setValue` on an unregistered field can silently skip the dirty-state
  // recompute entirely, leaving `isDirty` (and therefore staging) permanently
  // false while the field itself visibly changes. Comparing serialized values
  // directly sidesteps RHF's dirty machinery altogether, so it can't regress
  // the same way if the installed RHF version's internals shift again.
  const seedSnapshotRef = useRef<string>('')

  // Bumped every time an update mutation completes, so the "Used on"
  // (ContentBlockUsages) panel below the Update button refetches and shows
  // what was just saved instead of the snapshot from when the form opened.
  const [usagesRefreshKey, setUsagesRefreshKey] = useState(0)

  // --- Data fetching (edit mode) ---

  const {
    data: { findUniqueContentBlock: contentBlockData } = {},
    loading: loadingData,
  } = useQuery(GET_CONTENT_BLOCK, {
    variables: { id: blockId },
    fetchPolicy: 'network-only',
    skip: !blockId,
  })

  // --- Form setup ---

  const form = useForm<ContentBlockFormValues>({
    resolver: zodResolver(contentBlockFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      content_block_profiles: [],
      content_block_type: 'only_text',
      style: undefined,
      images: [],
      gallery_images: [],
      grid_product_picks: [],
      video_link: '',
      video_type: undefined,
      video_autoplay: false,
      video_loop: false,
      video_controls: true,
      default_margins: true,
      object_fit_cover: false,
      extra_css: '',
      text_color: '',
      background_color: '',
      link_new_tab: false,
      link_page_id: '',
      cta_new_tab: false,
      cta_link_page_id: '',
      cta_link_type: null,
      cta_link_category_id: null,
      linked_items: [],
      selected_category_ids: [],
      product_grid_mode: 'newest',
      product_grid_max_products: 8,
      product_grid_tag_ids: [],
      page_grid_mode: 'manual',
      page_grid_max_pages: 8,
      page_grid_items_per_row: 4,
      selected_page_ids: [],
      page_grid_parent_id: '',
      page_grid_show_tags: false,
      mediaRatio: '50_50',
      email_signup_delivery: 'email',
      email_signup_notify_email: '',
      pdf_flipbook_pdf_url: null,
      pdf_flipbook_manifest_url: null,
      pdf_flipbook_show_download_button: false,
      pdf_flipbook_viewer_mode: 'flipbook',
    },
  })

  const selectedType = form.watch('content_block_type')
  const showImageUpload = imageTypes.includes(selectedType)
  const showVideoInput = videoTypes.includes(selectedType)
  const selectedVideoType = form.watch('video_type')
  const isGallery = selectedType === 'gallery'
  const isTextAndImage = selectedType === 'text_and_image'
  const isButton = selectedType === 'button'
  const showStyleDropdown = selectedType === 'only_text' || isGallery || isTextAndImage || isButton
  const showLinkConfig = isButton
  const showDescription = !isButton && !hideDescription
  const isPhotoLinks = selectedType === 'photo_links'
  const isCategoryTiles = selectedType === 'category_tiles'
  const isProductGrid = selectedType === 'product_grid'
  const productGridMode = form.watch('product_grid_mode')
  const isPageGrid = selectedType === 'page_grid'
  const pageGridMode = form.watch('page_grid_mode')
  const isEmailSignup = selectedType === 'email_signup'
  const emailSignupDelivery = form.watch('email_signup_delivery')
  const showRecipientEmail = (emailSignupDelivery || 'email') === 'email'
  const isPdfFlipbook = selectedType === 'pdf_flipbook'
  const selectedStyle = form.watch('style')
  // Staged mode only: force a re-render on EVERY field change (mirrors the
  // named watches above, which already do this for their own fields) so the
  // effect below reads the live values at RENDER time. This matters: read
  // from inside an imperative `form.watch(callback)` subscription instead,
  // and the read can lag by one edit (react-hook-form applies its own state
  // updates in a way that isn't visible until the next render) —
  // render-time reads don't have that problem, which is exactly why this
  // codebase's other `onDirtyChange` forms (ProductFeatureForm, FabricForm)
  // read their signal as a dependency array value rather than inside a
  // callback. This value is compared (serialized) against `seedSnapshotRef`
  // rather than relying on `form.formState.isDirty` — see that ref's doc.
  const liveFormValues = form.watch()

  // --- Page selection (page_grid) ---

  const currentLang = allLanguages[languageId] || allLanguages[0]

  const { data: pagesData, loading: loadingPages } = useQuery(GET_PAGES_FOR_SELECTOR)
  const allPages = pagesData?.findManyPage || []

  const getPageTitle = (page: {
    code: string
    page_profiles: { title: string; language: string }[]
  }) => {
    return (
      page.page_profiles?.find(p => p.language === currentLang)?.title ||
      page.code
    )
  }

  const selectedPageIds = form.watch('selected_page_ids') || []
  const selectedPageIdSet = new Set(selectedPageIds)
  const unassignedPages = allPages.filter(
    (p: { id: string }) => !selectedPageIdSet.has(p.id)
  )

  // `selected_page_ids` is never registered via `FormField`/`Controller`/
  // `useFieldArray` (it's read/written entirely through `getValues`/`setValue`/
  // `watch`), so `{ shouldDirty: true }` here is defensive belt-and-braces —
  // the staging effect below no longer relies on `isDirty` for this reason
  // (see `seedSnapshotRef`'s doc) — but it's cheap, matches this codebase's
  // established convention for mutating unregistered fields (see
  // `TitleAlignmentControl`), and keeps the immediate-save (Dialog) consumers'
  // `form.formState.isDirty` accurate for anyone who inspects it directly.
  const handleAddPage = (pageId: string) => {
    const current = form.getValues('selected_page_ids') || []
    form.setValue('selected_page_ids', [...current, pageId], { shouldDirty: true })
  }

  const handleRemovePage = (pageId: string) => {
    const current = form.getValues('selected_page_ids') || []
    form.setValue(
      'selected_page_ids',
      current.filter(id => id !== pageId),
      { shouldDirty: true }
    )
  }

  const handlePageDragEnd = (result: any) => {
    if (!result.destination) return
    const current = [...(form.getValues('selected_page_ids') || [])]
    const [moved] = current.splice(result.source.index, 1)
    current.splice(result.destination.index, 0, moved)
    form.setValue('selected_page_ids', current, { shouldDirty: true })
  }

  // --- Category selection (category_tiles) ---

  const [loadCategories, { data: categoriesData, loading: loadingCategories }] =
    useLazyQuery(GET_CATEGORIES_FOR_SELECT, {
      fetchPolicy: 'cache-and-network',
    })

  useEffect(() => {
    if (isCategoryTiles) {
      loadCategories()
    }
  }, [isCategoryTiles, loadCategories])

  const allCategories = categoriesData?.findManyCategory || []

  const getCategoryName = (profiles: { name: string; language: string }[]) => {
    return (
      profiles.find(p => p.language === currentLang)?.name ||
      profiles[0]?.name ||
      'Unknown'
    )
  }

  const selectedCategoryIds = form.watch('selected_category_ids') || []
  const selectedCategoryIdSet = new Set(selectedCategoryIds)
  const unassignedCategories = allCategories.filter(
    (c: { id: number }) => !selectedCategoryIdSet.has(c.id)
  )

  // Same unregistered-field convention as `handleAddPage`/`handleRemovePage`/
  // `handlePageDragEnd` above — see that block's doc.
  const handleAddCategory = (categoryId: number) => {
    const current = form.getValues('selected_category_ids') || []
    form.setValue('selected_category_ids', [...current, categoryId], {
      shouldDirty: true,
    })
  }

  const handleRemoveCategory = (categoryId: number) => {
    const current = form.getValues('selected_category_ids') || []
    form.setValue(
      'selected_category_ids',
      current.filter(id => id !== categoryId),
      { shouldDirty: true }
    )
  }

  const handleCategoryDragEnd = (result: any) => {
    if (!result.destination) return
    const current = [...(form.getValues('selected_category_ids') || [])]
    const [moved] = current.splice(result.source.index, 1)
    current.splice(result.destination.index, 0, moved)
    form.setValue('selected_category_ids', current, { shouldDirty: true })
  }

  // --- Mutations ---

  // Emit the FULL block object (preview fields included) so the on-page panel
  // can render an accurate preview without waiting for a (guarded) page refetch.
  const toBlockItem = (block: any): ContentBlockItem => ({
    ...block,
    style: block.style || null,
    arrangement: block.arrangement ?? null,
  })

  const onCreateCompleted = (response: any) => {
    toast.success('Content block created successfully')
    onCreated(toBlockItem(response.createNewContentBlock))
  }

  const onUpdateCompleted = (response: any) => {
    toast.success('Content block updated successfully')
    onUpdated(toBlockItem(response.updateExistingContentBlock))
    setUsagesRefreshKey(k => k + 1)
  }

  const mutationOpts = {
    onError: () => { toast.error('Failed to save content block') },
    refetchQueries: ['GET_CONTENT_BLOCKS', 'GetContentBlock'],
  }

  const [createContentBlock, { loading: creating }] = useMutation(CREATE_CONTENT_BLOCK, {
    ...mutationOpts,
    onCompleted: onCreateCompleted,
  })

  const [createLinkedContentBlock, { loading: creatingLinked }] = useMutation(CREATE_LINKED_CONTENT_BLOCK, {
    ...mutationOpts,
    onCompleted: onCreateCompleted,
  })

  const [updateContentBlock, { loading: updating }] = useMutation(UPDATE_CONTENT_BLOCK, {
    ...mutationOpts,
    onCompleted: onUpdateCompleted,
  })

  const [updateLinkedContentBlock, { loading: updatingLinked }] = useMutation(UPDATE_LINKED_CONTENT_BLOCK, {
    ...mutationOpts,
    onCompleted: onUpdateCompleted,
  })

  const isSaving = creating || creatingLinked || updating || updatingLinked

  // --- Form population ---

  useEffect(() => {
    if (!loadingData) {
      if (contentBlockData && isEditMode) {
        // Keep the raw loaded config so onSubmit can spread it first.
        rawConfigRef.current = contentBlockData.config ?? {}
        const existingProfiles =
          contentBlockData.content_block_profiles?.filter(
            (p: { language: string }) => p.language && allLanguages.includes(p.language)
          ) ?? []

        const allProfiles = allLanguages.map(lang => {
          const existingProfile = existingProfiles.find(
            (p: { language: string }) => p.language === lang
          )
          if (existingProfile) {
            return {
              id: existingProfile.id,
              name: existingProfile.name ?? '',
              description: existingProfile.description ?? '',
              description_format: existingProfile.description_format ?? 'plain',
              link: existingProfile.link ?? '',
              cta_label: existingProfile.cta_label ?? '',
              cta_link: existingProfile.cta_link ?? '',
              language: existingProfile.language,
            }
          } else {
            return {
              name: '',
              description: '',
              description_format: 'plain' as const,
              link: '',
              cta_label: '',
              cta_link: '',
              language: lang,
            }
          }
        })

        const serverValues: ContentBlockFormValues = {
          content_block_profiles: allProfiles,
          content_block_type: contentBlockData.type || 'only_text',
          style: contentBlockData.style || undefined,
          images: contentBlockData.main_image
            ? [contentBlockData.main_image.src]
            : [],
          gallery_images:
            contentBlockData.gallery_images?.map((img: { src: string }) => img.src) ?? [],
          grid_product_picks: contentBlockData.grid_product_containers || [],
          video_link: contentBlockData.video_link || '',
          video_type: contentBlockData.video_type || undefined,
          video_autoplay: false,
          video_loop: false,
          default_margins: true,
          object_fit_cover: false,
          extra_css: contentBlockData.extra_css
            ? JSON.stringify(contentBlockData.extra_css, null, 2)
            : '',
          text_color: contentBlockData.text_color || '',
          background_color: contentBlockData.background_color || '',
          link_new_tab: contentBlockData.link_new_tab || false,
          link_page_id: contentBlockData.link_page?.id || '',
          cta_new_tab: contentBlockData.cta_new_tab || false,
          cta_link_page_id:
            contentBlockData.cta_link_page_id ||
            contentBlockData.cta_link_page?.id ||
            '',
          cta_link_type: contentBlockData.cta_link_type ?? null,
          cta_link_category_id: contentBlockData.cta_link_category?.id ?? null,
          linked_items: contentBlockData.linked_items?.map((item: any) => ({
            id: item.id,
            title: item.title,
            link: item.link,
            arrangement: item.arrangement,
            image: item.image?.src || '',
            imageId: item.image?.id || null,
          })) ?? [],
          selected_category_ids: contentBlockData.config?.category_ids ?? [],
          // `config.mode` is shared by both grid types but its allowed values
          // differ ('newest'|'manual' vs 'manual'|'children'), so only read it
          // into the field whose block type matches — otherwise a page_grid in
          // 'children' mode sets an invalid product_grid_mode and blocks submit.
          product_grid_mode:
            contentBlockData.type === 'product_grid'
              ? (contentBlockData.config as any)?.mode || 'newest'
              : 'newest',
          product_grid_max_products: (contentBlockData.config as any)?.max_products || 8,
          product_grid_tag_ids:
            contentBlockData.type === 'product_grid'
              ? ((contentBlockData.config as any)?.tag_ids ?? [])
              : [],
          page_grid_mode:
            contentBlockData.type === 'page_grid'
              ? (contentBlockData.config as any)?.mode || 'manual'
              : 'manual',
          page_grid_max_pages: (contentBlockData.config as any)?.max_pages || 8,
          page_grid_items_per_row:
            (contentBlockData.config as any)?.items_per_row || 4,
          selected_page_ids: (contentBlockData.config as any)?.page_ids ?? [],
          page_grid_parent_id: (contentBlockData.config as any)?.parent_id || '',
          page_grid_show_tags: (contentBlockData.config as any)?.show_tags ?? false,
          email_signup_delivery:
            (contentBlockData.config as any)?.delivery || 'email',
          email_signup_notify_email:
            (contentBlockData.config as any)?.notify_email || '',
          pdf_flipbook_pdf_url: (contentBlockData.config as any)?.pdf_url ?? null,
          pdf_flipbook_manifest_url:
            (contentBlockData.config as any)?.manifest_url ?? null,
          pdf_flipbook_show_download_button: Boolean(
            (contentBlockData.config as any)?.show_download_button
          ),
          // Absent/unknown ⇒ 'flipbook' (backward compat for blocks saved
          // before this key existed).
          pdf_flipbook_viewer_mode:
            (contentBlockData.config as any)?.viewer_mode === 'plain'
              ? 'plain'
              : 'flipbook',
          titleAlignment:
            contentBlockData.config?.titleAlignment ??
            naturalTitleAlign(contentBlockData.style),
          hideTitle: Boolean(contentBlockData.config?.hideTitle),
          textOnImageDisplay:
            (contentBlockData.config as any)?.display ?? 'full_width',
          mediaRatio: (contentBlockData.config as any)?.mediaRatio ?? '50_50',
          video_controls:
            (contentBlockData.config as any)?.video_controls ?? true,
        }

        // Staged mode: prefer the caller's previously staged snapshot (if any)
        // over the freshly-fetched server data, so reopening this same block
        // mid-session (before the page Save flushes it) shows what the user
        // already staged instead of quietly reverting it — see `draft` prop doc.
        // (The `onDraftChange` subscription below is gated on `seedSnapshotRef`,
        // which this `reset()` establishes as the new baseline below, so this
        // reset itself never reports a change.)
        const baseValues = isStaged && draft ? draft : serverValues
        // Fold the same inline-title overlay the flush applies (see
        // `nameOverrides` prop doc) so this SEED matches what a page Save
        // would actually send — whether the base came from an earlier
        // settings-form snapshot or fresh server data. Skipped when there's
        // nothing to overlay so non-staged (Dialog) callers are untouched.
        const seedValues =
          isStaged && nameOverrides
            ? {
                ...baseValues,
                content_block_profiles: overlayProfileNames(
                  baseValues.content_block_profiles,
                  nameOverrides
                ),
              }
            : baseValues
        // Snapshot the per-language name this seed carries — see
        // `seededNameByLanguageRef`'s doc. Captured HERE (not derived later)
        // so it reflects exactly what `form.reset` below is about to set,
        // regardless of which branch (`draft` vs fresh server data, overlaid
        // or not) produced it.
        seededNameByLanguageRef.current = Object.fromEntries(
          seedValues.content_block_profiles.map(p => [p.language, p.name ?? ''])
        )
        // Snapshot the seed itself — see `seedSnapshotRef`'s doc. Must be
        // captured from the SAME object passed to `form.reset` below (not
        // re-derived from `form.getValues()` afterwards) so it reflects
        // exactly the baseline `reset()` is about to establish.
        seedSnapshotRef.current = JSON.stringify(seedValues)
        form.reset(seedValues)
      } else if (!isEditMode) {
        rawConfigRef.current = {}
        // Staging is never active here in practice — `onDraftChange` is only
        // ever wired up for existing blocks (see `BlockEditPanel`), and this
        // branch only runs when `!isEditMode` (`blockId === null`) — but the
        // snapshot is still captured for symmetry with the branch above, so
        // the staging effect's gate is correct even if that ever changes.
        const createDefaults: ContentBlockFormValues = {
          content_block_profiles: allLanguages.map(lang => ({
            name: '',
            description: '',
            description_format: 'plain' as const,
            link: '',
            cta_label: '',
            cta_link: '',
            language: lang,
          })),
          content_block_type: 'only_text',
          style: undefined,
          images: [],
          gallery_images: [],
          grid_product_picks: [],
          video_link: '',
          video_type: undefined,
          video_autoplay: false,
          video_loop: false,
          default_margins: true,
          object_fit_cover: false,
          extra_css: '',
          text_color: '',
          background_color: '',
          link_new_tab: false,
          link_page_id: '',
          cta_new_tab: false,
          cta_link_page_id: '',
          cta_link_type: null,
          cta_link_category_id: null,
          linked_items: [],
          selected_category_ids: [],
          product_grid_mode: 'newest',
          product_grid_max_products: 8,
          product_grid_tag_ids: [],
          page_grid_mode: 'manual',
          page_grid_max_pages: 8,
          page_grid_items_per_row: 4,
          selected_page_ids: [],
          page_grid_parent_id: '',
          page_grid_show_tags: false,
          email_signup_delivery: 'email',
          email_signup_notify_email: '',
          pdf_flipbook_pdf_url: null,
          pdf_flipbook_manifest_url: null,
          pdf_flipbook_show_download_button: false,
          pdf_flipbook_viewer_mode: 'flipbook',
          titleAlignment: naturalTitleAlign(undefined),
          hideTitle: false,
          textOnImageDisplay: 'full_width',
          mediaRatio: '50_50',
          video_controls: true,
        }
        seedSnapshotRef.current = JSON.stringify(createDefaults)
        form.reset(createDefaults)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentBlockData, loadingData, allLanguages, isEditMode])

  // Create mode only: keep `titleAlignment` tracking the selected style's
  // natural default until the user manually overrides the control (once the
  // field is dirty we leave it alone). Edit mode seeds from stored config/style
  // above and must not be re-derived here.
  useEffect(() => {
    if (isEditMode) return
    if (form.formState.dirtyFields.titleAlignment) return
    form.setValue('titleAlignment', naturalTitleAlign(selectedStyle))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStyle, isEditMode])

  // `onDraftChange` is passed as a fresh inline closure by the caller
  // (BlockEditPanel/PageDetails) on every one of ITS renders — and calling it
  // below triggers exactly such a re-render (it stages into PageDetails'
  // state). Depending on `onDraftChange` itself (rather than a ref to it)
  // would therefore re-fire this effect after every call, forever. A ref
  // sidesteps that: we always invoke the LATEST callback, but re-render churn
  // in the callback's IDENTITY alone can't retrigger the effect.
  const onDraftChangeRef = useRef(onDraftChange)
  useEffect(() => {
    onDraftChangeRef.current = onDraftChange
  })

  // Latches to `true` on the first genuine edit this mount (the first time
  // the live values diverge from `seedSnapshotRef`) and then STAYS `true` for
  // the rest of this mount — see the staging effect below for why. Reset per
  // block: `BlockEditPanel` mounts this component with `key={blockId}`, so
  // switching blocks (or closing + reopening this one) creates a fresh ref,
  // and on reopen the form's baseline IS the previously staged draft (see
  // `draft` prop), so "still equals the seed" there correctly means "still
  // equals the draft" and nothing (re-)stages until a genuine edit happens
  // again.
  const hasStagedRef = useRef(false)

  // Staged mode only (`onDraftChange` present): report every settings-form
  // change up to the caller instead of mutating.
  //
  // The FIRST stage of a mount is gated on comparing the live form values
  // (serialized) against `seedSnapshotRef` — the snapshot `reset()`
  // (population effect, above) established as this mount's baseline — rather
  // than react-hook-form's own `formState.isDirty`. This is NOT merely a
  // style choice: `selected_page_ids` (the page_grid page picker below) is
  // mutated via a bare `form.setValue(...)` on a path that's never registered
  // through `FormField`/`Controller`/`useFieldArray`, and RHF's dirty-state
  // recompute for an unregistered `setValue` is an internal implementation
  // detail that has changed across minor versions — relying on it left this
  // exact field's edits invisible to `isDirty` (and therefore silently
  // unstaged) under some installed RHF versions despite the panel visibly
  // updating. Comparing serialized values directly doesn't depend on RHF's
  // dirty machinery at all, so it can't regress the same way again. Skipped
  // while `loadingData` is still true (edit mode, before the population
  // effect's first `reset()` establishes a real seed) so a slow
  // `GET_CONTENT_BLOCK` can't compare live values against the stale empty
  // snapshot from before this mount had real data. `liveFormValues` (the
  // whole-form watch above) is in the dependency array so this fires on every
  // subsequent keystroke too, not just the seed's initial equal→unequal flip.
  // (Per-field deep-diffing is intentionally NOT done — see PageDetails'
  // `dirty` computation for why.)
  //
  // Once staged, `hasStagedRef` LATCHES and every later run of this effect
  // reports the current values regardless of whether they currently match the
  // seed. This matters because that comparison is NOT monotonic: reverting a
  // field back to the `reset()` baseline (e.g. typing a char then backspacing
  // it out) makes the live values equal the seed again even though
  // `liveFormValues` genuinely changed (twice) in between. Gating every run
  // on the seed comparison (as a prior version of this effect did with
  // `isDirty`) would early-return on that revert and leave the caller's draft
  // holding the PRE-revert snapshot forever — the field would show the
  // reverted value while a later page Save silently wrote the stale one. The
  // latch avoids that without dropping the "opening a panel alone must not
  // stage a draft" guard: it only takes effect at all AFTER the first genuine
  // edit's equal→unequal flip.
  //
  // Stages the RAW `form.getValues()` — unfiltered and NOT run through
  // `contentBlockFormSchema`. A previous version of this effect staged the
  // schema's PARSED output instead (to match what the immediate-save path's
  // `form.handleSubmit` feeds `onSubmit`), but that parsed output has its
  // `content_block_profiles` FILTERED (empty placeholder profiles dropped),
  // and this same staged snapshot is fed straight back into `form.reset()`
  // the next time this block's panel is reopened (see `draft` prop above,
  // and the population effect's `baseValues`/`seedValues`). Filtering here
  // collapses that array and shifts every later language's profile onto the
  // wrong index — the form addresses profiles BY INDEX
  // (`content_block_profiles.${languageId}.name`, keyed off
  // `LanguageContext`'s positional `languageId`), so this silently corrupted
  // data across languages. Filtering + full validation now happens ONCE, at
  // flush time, in `PageDetails.flushBlockDraft` via the SAME
  // `contentBlockFormSchema.safeParse` immediately before
  // `buildContentBlockVariables` — so the payload this eventually produces is
  // unaffected by staging raw values here.
  //
  // Also reports `seededNameByLanguageRef.current` — the per-language name
  // this form was SEEDED with (see that ref's doc) — alongside the values, so
  // the caller can tell "the settings Title differs from an inline override
  // because the user just retyped it here" apart from "...because this form
  // was seeded before that override existed" (see `PageDetails.handleStageFormValues`).
  //
  // `form.trigger()` still runs on every change so `formState.errors`
  // populates and the same `FormMessage` fields used by the immediate-save
  // path show inline errors as the user types — that inline UX is unchanged.
  // What's gone is refusing to STAGE invalid values (and the page-level Save
  // refusal that used to depend on that, via `onValidityChange`): an invalid
  // edit is now staged like any other and, if it's still invalid when the
  // page Save runs, reported as an ordinary per-block flush failure alongside
  // any other block that failed to save (see `PageDetails.flushBlockDraft`).
  useEffect(() => {
    if (!isStaged || loadingData) return
    const liveSnapshot = JSON.stringify(liveFormValues)
    if (!hasStagedRef.current && liveSnapshot === seedSnapshotRef.current) return
    hasStagedRef.current = true
    onDraftChangeRef.current?.(form.getValues(), seededNameByLanguageRef.current)
    void form.trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaged, loadingData, isStaged ? JSON.stringify(liveFormValues) : null])

  // --- Submit handler ---

  function onSubmit(values: ContentBlockFormValues) {
    // PURE payload construction — shared with the page editor's deferred flush
    // (`PageDetails.onSave`) so both build an IDENTICAL payload from identical
    // inputs and can't drift apart. See `contentBlockPayload.ts`.
    const variables = buildContentBlockVariables(values, {
      hideDescription,
      rawConfig: rawConfigRef.current,
      isCreate: !isEditMode,
    })
    const isLinkedType = isLinkedContentBlockType(values.content_block_type)

    if (isEditMode && blockId) {
      const mutate = isLinkedType ? updateLinkedContentBlock : updateContentBlock
      mutate({
        variables: {
          where: { id: blockId },
          ...variables,
        },
      })
    } else {
      const mutate = isLinkedType ? createLinkedContentBlock : createContentBlock
      mutate({ variables })
    }
  }

  // --- Render ---

  if (loadingData && isEditMode) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className={`${bodyScrollClassName} pr-2`.trim()}>
      <Form {...form}>
        <form
          onSubmit={e => {
            e.preventDefault()
            e.stopPropagation()
            // Staged mode has no submit button, but a bare <form> still
            // implicitly submits on Enter inside a text input — guard against
            // that firing a real mutation behind the deferred flow's back.
            if (isStaged) return
            // Surface validation failures: some invalid fields belong to block
            // types that aren't currently rendered, so without this the Update
            // button would silently do nothing.
            form.handleSubmit(onSubmit, () => {
              toast.error('Some fields are invalid — please review the form.')
            })(e)
          }}
          className="space-y-4 py-2"
        >
          <div className="grid gap-4">
            {/* Type selector */}
            <InputFieldContainer
              label="Type"
              description="Select the content block type"
              control={form.control}
              name="content_block_type"
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {contentBlockTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Style selector (conditional) */}
            {showStyleDropdown && (
              <InputFieldContainer
                label="Style"
                description="Visual layout style for this content block"
                control={form.control}
                name="style"
                render={({ field }) => {
                  const styleOptions = isButton
                    ? buttonStyles
                    : isGallery
                      ? galleryStyles
                      : isTextAndImage
                        ? textAndImageStyles
                        : textStyles
                  return (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <StyleSelector
                          options={styleOptions}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
            )}

            {/* Title */}
            <InputFieldContainer
              label="Title"
              description="Title for this content block"
              control={form.control}
              name={`content_block_profiles.${languageId}.name`}
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <FormControl>
                    <Input placeholder="" type="text" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Title alignment */}
            <TitleAlignmentControl form={form} />

            {/* Description */}
            {showDescription && (
              <>
                <InputFieldContainer
                  label="Description format"
                  control={form.control}
                  name={`content_block_profiles.${languageId}.description_format`}
                  render={({
                    field,
                  }: {
                    field: {
                      value: 'plain' | 'markdown'
                      onChange: (value: 'plain' | 'markdown') => void
                    }
                  }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              checked={(field.value ?? 'plain') === 'plain'}
                              onChange={() => field.onChange('plain')}
                            />
                            <span className="text-sm">Plain</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              checked={field.value === 'markdown'}
                              onChange={() => field.onChange('markdown')}
                            />
                            <span className="text-sm">Markdown</span>
                          </label>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <InputFieldContainer
                  label="Description"
                  control={form.control}
                  name={`content_block_profiles.${languageId}.description`}
                  render={({ field }: { field: any }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <RichTextField
                          field={field}
                          format={
                            form.watch(
                              `content_block_profiles.${languageId}.description_format`
                            ) ?? 'plain'
                          }
                          rows={6}
                          placeholder="Description"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            {/* CTA button (Text on Image, Side by Side, Image Left styles) */}
            {isTextAndImage &&
              (selectedStyle === 'text_on_image' ||
                selectedStyle === 'side_by_side' ||
                selectedStyle === 'image_left') && (
                <CtaEditor
                  control={form.control}
                  languageId={languageId}
                  labelName={`content_block_profiles.${languageId}.cta_label`}
                  linkName={`content_block_profiles.${languageId}.cta_link`}
                  newTabName="cta_new_tab"
                  linkPageIdName="cta_link_page_id"
                  linkTypeName="cta_link_type"
                  linkCategoryIdName="cta_link_category_id"
                />
              )}

            {/* Display width (Text on Image style only) */}
            {isTextAndImage && selectedStyle === 'text_on_image' && (
              <InputFieldContainer
                label="Display"
                description="Whether the block spans the full page width or is constrained to the content width"
                control={form.control}
                name="textOnImageDisplay"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || 'full_width'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select display" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="full_width">Full width</SelectItem>
                        <SelectItem value="content_width">Content width</SelectItem>
                        <SelectItem value="text_width">Text width</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Image / Text width ratio (Side by Side & Image Left styles only) */}
            {isTextAndImage &&
              (selectedStyle === 'side_by_side' ||
                selectedStyle === 'image_left') && (
                <InputFieldContainer
                  label="Image / Text ratio"
                  description="How the image and text columns split the row (image : text)"
                  control={form.control}
                  name="mediaRatio"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || '50_50'}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select ratio" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="50_50">
                            Image 50% / Text 50%
                          </SelectItem>
                          <SelectItem value="60_40">
                            Image 60% / Text 40%
                          </SelectItem>
                          <SelectItem value="40_60">
                            Image 40% / Text 60%
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

            {/* Link configuration (button type) */}
            {showLinkConfig && (
              <>
                <InputFieldContainer
                  label="Link Type"
                  control={form.control}
                  name="link_page_id"
                  render={({ field }: { field: { value: string; onChange: (value: string) => void } }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              checked={!field.value}
                              onChange={() => field.onChange('')}
                            />
                            <span className="text-sm">External URL</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              checked={!!field.value}
                              onChange={() => field.onChange('select')}
                            />
                            <span className="text-sm">Internal Page</span>
                          </label>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!form.watch('link_page_id') ? (
                  <>
                    <InputFieldContainer
                      label="URL"
                      control={form.control}
                      name={`content_block_profiles.${languageId}.link`}
                      render={({ field }: { field: React.InputHTMLAttributes<HTMLInputElement> }) => (
                        <FormItem className="col-span-3">
                          <FormControl>
                            <Input
                              placeholder="https://example.com"
                              type="text"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <InputFieldContainer
                      label="New Tab"
                      control={form.control}
                      name="link_new_tab"
                      render={({ field }: { field: { value: boolean; onChange: (value: boolean) => void } }) => (
                        <FormItem className="col-span-3">
                          <FormControl>
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                              <Label className="text-sm font-normal">Open in new tab</Label>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                ) : (
                  <InputFieldContainer
                    label="Page"
                    control={form.control}
                    name="link_page_id"
                    render={({ field }: { field: { value: string; onChange: (value: string) => void } }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <PageSelector
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </>
            )}

            {/* Image upload (single, non-gallery) */}
            {showImageUpload && !isGallery && (
              <InputFieldContainer
                label="Image"
                description="Upload content block image"
                control={form.control}
                name="images"
                render={field => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <ImageUpload maxImages={1} variant="large" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Gallery images */}
            {isGallery && (
              <InputFieldContainer
                label="Gallery Images"
                description="Upload multiple images for the gallery"
                control={form.control}
                name="gallery_images"
                render={field => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <ImageUpload
                        maxImages={10}
                        variant="large"
                        enableReordering
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {isGallery && (
              <InputFieldContainer
                label="Display"
                description="Whether the block spans the full page width, the content width, or the narrower text width"
                control={form.control}
                name="textOnImageDisplay"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || 'full_width'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select display" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="full_width">
                          Full width
                        </SelectItem>
                        <SelectItem value="content_width">
                          Content width
                        </SelectItem>
                        <SelectItem value="text_width">
                          Text width
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Email signup */}
            {isEmailSignup && (
              <>
                <InputFieldContainer
                  label="Delivery"
                  control={form.control}
                  name="email_signup_delivery"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || 'email'}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select delivery" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="mailerlite">MailerLite</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {showRecipientEmail && (
                  <InputFieldContainer
                    label="Recipient Email"
                    description="Leave empty to use Partner Request Email"
                    control={form.control}
                    name="email_signup_notify_email"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Input
                            placeholder="name@example.com"
                            type="email"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </>
            )}

            {/* PDF flipbook */}
            {isPdfFlipbook && (
              <>
                <InputFieldContainer
                  label="Catalog PDF"
                  control={form.control}
                  name="pdf_flipbook_pdf_url"
                  render={() => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <PdfUploadV2
                          pdfUrl={form.watch('pdf_flipbook_pdf_url') ?? null}
                          manifestUrl={form.watch('pdf_flipbook_manifest_url') ?? null}
                          onChange={({ pdf_url, manifest_url }) => {
                            form.setValue('pdf_flipbook_pdf_url', pdf_url, { shouldDirty: true })
                            form.setValue('pdf_flipbook_manifest_url', manifest_url, { shouldDirty: true })
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <InputFieldContainer
                  label="Display as"
                  control={form.control}
                  name="pdf_flipbook_viewer_mode"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <Select
                        onValueChange={field.onChange}
                        value={field.value === 'plain' ? 'plain' : 'flipbook'}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select display mode" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="flipbook">Flipbook</SelectItem>
                          <SelectItem value="plain">Plain PDF</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <InputFieldContainer
                  label="Show download button"
                  control={form.control}
                  name="pdf_flipbook_show_download_button"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Switch checked={Boolean(field.value)} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            {/* Photo links */}
            {isPhotoLinks && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Linked Items</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const current = form.getValues('linked_items') || []
                      if (current.length < 6) {
                        form.setValue(
                          'linked_items',
                          [
                            ...current,
                            {
                              title: '',
                              link: '',
                              arrangement: current.length,
                              image: '',
                              imageId: null,
                            },
                          ],
                          { shouldDirty: true }
                        )
                      }
                    }}
                  >
                    + Add Item
                  </Button>
                </div>
                {(form.watch('linked_items') || []).map((item, index) => (
                  <div key={index} className="border rounded-md p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        Item {index + 1}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const current = form.getValues('linked_items') || []
                          form.setValue(
                            'linked_items',
                            current.filter((_, i) => i !== index),
                            { shouldDirty: true }
                          )
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                    <Input
                      placeholder="Title"
                      value={item.title}
                      onChange={e => {
                        const current = form.getValues('linked_items') || []
                        current[index] = {
                          ...current[index],
                          title: e.target.value,
                        }
                        form.setValue('linked_items', [...current], {
                          shouldDirty: true,
                        })
                      }}
                    />
                    <Input
                      placeholder="Link (e.g., /en/categories/sofas)"
                      value={item.link}
                      onChange={e => {
                        const current = form.getValues('linked_items') || []
                        current[index] = {
                          ...current[index],
                          link: e.target.value,
                        }
                        form.setValue('linked_items', [...current], {
                          shouldDirty: true,
                        })
                      }}
                    />
                    {/* Image upload */}
                    <ImageUploadV2
                      field={
                        {
                          name: `linked_items.${index}.imageId`,
                          value: item.imageId,
                          onChange: (newImageId: number | null) => {
                            const current = form.getValues('linked_items') || []
                            current[index] = {
                              ...current[index],
                              imageId: newImageId,
                              image: '',
                            }
                            form.setValue('linked_items', [...current], {
                              shouldDirty: true,
                            })
                          },
                          onBlur: () => {},
                          ref: () => {},
                        } as any
                      }
                      imageName={`content-block-linked-item-${index}`}
                      existingImageUrl={item.image || undefined}
                    />
                    {/* Fallback URL input when no upload */}
                    {!item.imageId && (
                      <Input
                        placeholder="Or paste image URL"
                        value={item.image || ''}
                        onChange={e => {
                          const current = form.getValues('linked_items') || []
                          current[index] = {
                            ...current[index],
                            image: e.target.value,
                            imageId: null,
                          }
                          form.setValue('linked_items', [...current], {
                            shouldDirty: true,
                          })
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Category tiles */}
            {isCategoryTiles && (
              <div className="space-y-4">
                {/* Selected Categories */}
                <div className="overflow-hidden">
                  <Label className="text-sm font-medium">
                    Selected Categories
                  </Label>
                  {selectedCategoryIds.length === 0 ? (
                    <p className="text-sm text-muted-foreground mt-2">
                      No categories selected
                    </p>
                  ) : (
                    <DragDropContext onDragEnd={handleCategoryDragEnd}>
                      <Droppable
                        droppableId="selected-categories"
                        direction="horizontal"
                      >
                        {provided => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className="mt-3 flex flex-wrap gap-3 pb-2"
                          >
                            {selectedCategoryIds.map((catId, index) => {
                              const cat = allCategories.find(
                                (c: any) => c.id === catId
                              )
                              const name = cat
                                ? getCategoryName(cat.category_profiles)
                                : `Category #${catId}`
                              const bannerSrc =
                                cat?.banners?.[0]?.src || cat?.image?.src
                              return (
                                <Draggable
                                  key={catId}
                                  draggableId={String(catId)}
                                  index={index}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      className={`relative flex-shrink-0 w-32 rounded-lg border overflow-hidden group ${
                                        snapshot.isDragging
                                          ? 'shadow-lg ring-2 ring-primary'
                                          : ''
                                      }`}
                                    >
                                      {/* Drag handle */}
                                      <div
                                        {...provided.dragHandleProps}
                                        className="absolute top-1 left-1 z-10 bg-background/80 rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab"
                                      >
                                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                                      </div>
                                      {/* Remove button */}
                                      <Button
                                        type="button"
                                        variant="destructive"
                                        size="icon"
                                        className="absolute top-1 right-1 z-10 h-6 w-6 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                                        onClick={() =>
                                          handleRemoveCategory(catId)
                                        }
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                      {/* Banner image */}
                                      <div className="aspect-[3/4] bg-muted">
                                        {bannerSrc ? (
                                          <img
                                            src={bannerSrc}
                                            alt={name}
                                            className="w-full h-full object-cover"
                                          />
                                        ) : (
                                          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                                            No image
                                          </div>
                                        )}
                                      </div>
                                      {/* Category name */}
                                      <div className="px-2 py-2 text-sm font-medium text-center truncate border-t">
                                        {name}
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              )
                            })}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  )}
                </div>

                {/* Available Categories */}
                <div>
                  <Label className="text-sm font-medium">
                    Available Categories
                  </Label>
                  {loadingCategories ? (
                    <p className="text-sm text-muted-foreground mt-2">
                      Loading categories...
                    </p>
                  ) : unassignedCategories.length === 0 ? (
                    <p className="text-sm text-muted-foreground mt-2">
                      All categories selected
                    </p>
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                      {unassignedCategories.map((cat: any) => {
                        const bannerSrc =
                          cat?.banners?.[0]?.src || cat?.image?.src
                        return (
                          <div
                            key={cat.id}
                            className="flex-shrink-0 w-28 rounded-lg border overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all"
                            onClick={() => handleAddCategory(cat.id)}
                          >
                            <div className="aspect-[3/4] bg-muted">
                              {bannerSrc ? (
                                <img
                                  src={bannerSrc}
                                  alt={getCategoryName(cat.category_profiles)}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                                  No image
                                </div>
                              )}
                            </div>
                            <div className="px-1.5 py-1.5 text-xs text-center truncate border-t flex items-center justify-between">
                              <span className="truncate">
                                {getCategoryName(cat.category_profiles)}
                              </span>
                              <Plus className="h-3 w-3 flex-shrink-0 ml-1" />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Product grid */}
            {isProductGrid && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Mode</label>
                  <Controller
                    name="product_grid_mode"
                    control={form.control}
                    render={({ field }) => (
                      <Select
                        value={field.value || 'newest'}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select mode" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="newest">Newest Products</SelectItem>
                          <SelectItem value="manual">Manual Selection</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Max Products</label>
                  <Controller
                    name="product_grid_max_products"
                    control={form.control}
                    render={({ field }) => (
                      <Input
                        type="number"
                        min={1}
                        max={8}
                        value={field.value || 8}
                        onChange={e =>
                          field.onChange(parseInt(e.target.value) || 8)
                        }
                      />
                    )}
                  />
                </div>
                {productGridMode !== 'manual' && (
                  <div>
                    <label className="text-sm font-medium">Filter by Tags</label>
                    <Controller
                      name="product_grid_tag_ids"
                      control={form.control}
                      render={({ field }) => <TagsSelect field={field} />}
                    />
                  </div>
                )}
                {productGridMode === 'manual' && (
                  <div>
                    <label className="text-sm font-medium">Select Products</label>
                    <Controller
                      name="grid_product_picks"
                      control={form.control}
                      render={({ field }) => <GridProductPicker field={field} />}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Page grid */}
            {isPageGrid && (
              <div className="space-y-4">
                <InputFieldContainer
                  label="Mode"
                  control={form.control}
                  name="page_grid_mode"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <Select
                        value={field.value || 'manual'}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select mode" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="manual">Manual Selection</SelectItem>
                          <SelectItem value="children">Child Pages</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <InputFieldContainer
                  label="Max Pages"
                  control={form.control}
                  name="page_grid_max_pages"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          max={24}
                          value={field.value || 8}
                          onChange={e =>
                            field.onChange(parseInt(e.target.value) || 8)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <InputFieldContainer
                  label="Items Per Row"
                  control={form.control}
                  name="page_grid_items_per_row"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Input
                          type="number"
                          min={2}
                          max={6}
                          value={field.value || 4}
                          onChange={e =>
                            field.onChange(parseInt(e.target.value) || 4)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <InputFieldContainer
                  label="Show Tags"
                  control={form.control}
                  name="page_grid_show_tags"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <p className="text-sm text-muted-foreground">
                  Child Pages mode lists the children of a page — by default the
                  page this block is attached to, or the Parent Page you choose
                  below.
                </p>
                {pageGridMode === 'children' && (
                  <>
                    <InputFieldContainer
                      label="Parent Page"
                      control={form.control}
                      name="page_grid_parent_id"
                      render={({ field }) => (
                        <FormItem className="col-span-3">
                          <Select
                            value={field.value ? field.value : '__current__'}
                            onValueChange={value =>
                              field.onChange(
                                value === '__current__' ? '' : value
                              )
                            }
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Current page (default)" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="__current__">
                                Current page (default)
                              </SelectItem>
                              {allPages.map((page: any) => (
                                <SelectItem key={page.id} value={page.id}>
                                  {getPageTitle(page)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <p className="text-sm text-muted-foreground">
                      Defaults to this page's children. Pick a page to show that
                      page's children instead — useful on the homepage, where
                      there is no current page.
                    </p>
                  </>
                )}
                {pageGridMode === 'manual' && (
                  <>
                    {/* Selected Pages */}
                    <div className="overflow-hidden">
                      <Label className="text-sm font-medium">Selected Pages</Label>
                      {selectedPageIds.length === 0 ? (
                        <p className="text-sm text-muted-foreground mt-2">
                          No pages selected
                        </p>
                      ) : (
                        <DragDropContext onDragEnd={handlePageDragEnd}>
                          <Droppable
                            droppableId="selected-pages"
                            direction="horizontal"
                          >
                            {provided => (
                              <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                className="mt-3 flex flex-wrap gap-3 pb-2"
                              >
                                {selectedPageIds.map((pageId, index) => {
                                  const page = allPages.find(
                                    (p: any) => p.id === pageId
                                  )
                                  const title = page ? getPageTitle(page) : pageId
                                  const imageSrc = page?.hero_image?.src
                                  return (
                                    <Draggable
                                      key={pageId}
                                      draggableId={pageId}
                                      index={index}
                                    >
                                      {(provided, snapshot) => (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          className={`relative flex-shrink-0 w-32 rounded-lg border overflow-hidden group ${
                                            snapshot.isDragging
                                              ? 'shadow-lg ring-2 ring-primary'
                                              : ''
                                          }`}
                                        >
                                          {/* Drag handle */}
                                          <div
                                            {...provided.dragHandleProps}
                                            className="absolute top-1 left-1 z-10 bg-background/80 rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab"
                                          >
                                            <GripVertical className="h-4 w-4 text-muted-foreground" />
                                          </div>
                                          {/* Remove button */}
                                          <Button
                                            type="button"
                                            variant="destructive"
                                            size="icon"
                                            className="absolute top-1 right-1 z-10 h-6 w-6 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                                            onClick={() =>
                                              handleRemovePage(pageId)
                                            }
                                          >
                                            <X className="h-3 w-3" />
                                          </Button>
                                          {/* Hero image */}
                                          <div className="aspect-[3/4] bg-muted">
                                            {imageSrc ? (
                                              <img
                                                src={imageSrc}
                                                alt={title}
                                                className="w-full h-full object-cover"
                                              />
                                            ) : (
                                              <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                                                No image
                                              </div>
                                            )}
                                          </div>
                                          {/* Page title */}
                                          <div className="px-2 py-2 text-sm font-medium text-center truncate border-t">
                                            {title}
                                          </div>
                                        </div>
                                      )}
                                    </Draggable>
                                  )
                                })}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </DragDropContext>
                      )}
                    </div>

                    {/* Available Pages */}
                    <div>
                      <Label className="text-sm font-medium">Available Pages</Label>
                      {loadingPages ? (
                        <p className="text-sm text-muted-foreground mt-2">
                          Loading pages...
                        </p>
                      ) : unassignedPages.length === 0 ? (
                        <p className="text-sm text-muted-foreground mt-2">
                          All pages selected
                        </p>
                      ) : (
                        <div className="mt-2 flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                          {unassignedPages.map((page: any) => {
                            const imageSrc = page?.hero_image?.src
                            return (
                              <div
                                key={page.id}
                                className="flex-shrink-0 w-28 rounded-lg border overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all"
                                onClick={() => handleAddPage(page.id)}
                              >
                                <div className="aspect-[3/4] bg-muted">
                                  {imageSrc ? (
                                    <img
                                      src={imageSrc}
                                      alt={getPageTitle(page)}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                                      No image
                                    </div>
                                  )}
                                </div>
                                <div className="px-1.5 py-1.5 text-xs text-center truncate border-t flex items-center justify-between">
                                  <span className="truncate">
                                    {getPageTitle(page)}
                                  </span>
                                  <Plus className="h-3 w-3 flex-shrink-0 ml-1" />
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Video fields */}
            {showVideoInput && (
              <>
                <InputFieldContainer
                  label="Video Type"
                  description="Select the video source type"
                  control={form.control}
                  name="video_type"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select video type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {videoTypeOptions.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <InputFieldContainer
                  label="Display"
                  description="Whether the block spans the full page width, the content width, or the narrower text width"
                  control={form.control}
                  name="textOnImageDisplay"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || 'full_width'}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select display" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="full_width">
                            Full width
                          </SelectItem>
                          <SelectItem value="content_width">
                            Content width
                          </SelectItem>
                          <SelectItem value="text_width">
                            Text width
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <InputFieldContainer
                  label={
                    selectedVideoType === 'uploaded' ? 'Video' : 'Video Link'
                  }
                  description={
                    selectedVideoType === 'uploaded'
                      ? 'Upload a video file (MP4 or WebM)'
                      : 'Enter the video URL (YouTube or Vimeo link)'
                  }
                  control={form.control}
                  name="video_link"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        {selectedVideoType === 'uploaded' ? (
                          <VideoUploadV2 field={field} />
                        ) : (
                          <Input
                            placeholder="https://www.youtube.com/watch?v=..."
                            type="text"
                            {...field}
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {selectedVideoType === 'uploaded' && (
                  <InputFieldContainer
                    label="Controls"
                    control={form.control}
                    name="video_controls"
                    render={({
                      field,
                    }: {
                      field: {
                        value: boolean
                        onChange: (value: boolean) => void
                      }
                    }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                            <Label className="text-sm font-normal">
                              Show video controls
                            </Label>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </>
            )}

            {/* Custom styles (JSON) */}
            <InputFieldContainer
              label="Custom Styles"
              description="Additional CSS styling as JSON"
              control={form.control}
              name="extra_css"
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <FormControl>
                    <StyleEditor
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Actions — staged mode (page editor) has no per-block Update/
                Cancel: changes bind live into the page's draft instead and
                commit on the page-level Save. */}
            {isStaged ? (
              <p className="text-xs text-muted-foreground">
                Changes here are applied when you save the page.
              </p>
            ) : (
              <>
                <div className="flex gap-2">
                  {onCancel && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={onCancel}
                      disabled={isSaving}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    type="submit"
                    disabled={isSaving || loadingData}
                    className="flex-1"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {isEditMode ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      isEditMode ? 'Update' : 'Create'
                    )}
                  </Button>
                </div>
                {isEditMode && blockId && (
                  <ContentBlockUsages key={usagesRefreshKey} id={blockId} />
                )}
              </>
            )}
          </div>
        </form>
      </Form>
    </div>
  )
}
